import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonNote,
  IonPopover,
  IonTitle,
  IonToolbar,
  useIonViewWillLeave
} from '@ionic/react';
import { addCircle, bookSharp, chatboxEllipses, chatbubbles, chevronDown, ellipsisVertical, heart, heartOutline, send, trashBin } from 'ionicons/icons';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import PostTypes, { PostTypesValues } from '../../data/PostTypes';
import UserRoles from '../../data/UserRoles';
import IFellowshipPost from '../../interfaces/IFellowshipPost';
import IUser from '../../interfaces/IUser';
import { AppRouteInfo, AppRouteKeys } from '../../routes/AppRoutes';
import './FellowshipPostCard.scss';
import { ReactComponent as PrayerIcon } from '../../assets/images/prayerLogo.svg';
import IFellowshipComment from '../../interfaces/IFellowshipComment';
import IGroupMembers, { GetMemberInfo } from '../../interfaces/IGroupMembers';
import PostVideos from '../PostVideos/PostVideos';

enum postCardStatus {
  new,
  updated,
  old,
}

interface IFellowshipPostCardProps {
  post: IFellowshipPost,
  currentUser: IUser,
  memberDetails: IGroupMembers,
  toggleLike: (post: IFellowshipPost) => void,
  savePrayer: (title: string, description: string, postId: string, displayName: string) => void,
  removePost: (post: IFellowshipPost) => void,
}

const FellowshipPostCard: React.FC<IFellowshipPostCardProps> = (props) => {
  const history = useHistory();
  const [showLikesUserList, setShowLikesUserList] = useState(false);
  const [likesUserListEvent, setLikesUserListEvent] = useState<Event | undefined>(undefined);
  const [showPostMenu, setShowPostMenu] = useState(false);
  const [postMenuEvent, setPostMenuEvent] = useState<Event | undefined>(undefined);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [currentPostCardStatus, setCurrentPostCardStatus] = useState<postCardStatus>(postCardStatus.old);
  const [lastModifiedDate, setLastModifiedDate] = useState<any>(new Date(0).getTime());
  const postCardRef = useRef<HTMLIonCardElement>(null);
  const removeNewTagTimer = useRef<undefined | number>(undefined);

  useIonViewWillLeave(() => {
    setShowLikesUserList(false); 
    setLikesUserListEvent(undefined); 
    setCurrentPostCardStatus(postCardStatus.old);
    window.clearInterval(removeNewTagTimer.current);
    removeNewTagTimer.current = undefined;
  });

  useEffect(() => {
    if(!removeNewTagTimer.current && currentPostCardStatus !== postCardStatus.old && isIntersecting) {
      removeNewTagTimer.current = window.setInterval(() => {
        setCurrentPostCardStatus(postCardStatus.old);
        window.clearInterval(removeNewTagTimer.current);
        removeNewTagTimer.current = undefined;
      }, 30000);
    }
  
    return () => {
      if(removeNewTagTimer.current) {
        window.clearInterval(removeNewTagTimer.current);
        removeNewTagTimer.current = undefined;
      }
    }
  }, [currentPostCardStatus, isIntersecting])

  // Set Post Status
  useEffect(() => {
    if(lastModifiedDate !== props.post.modifiedDate) {
      if(props.post.createdDate === props.post.modifiedDate && props.post.subscribed) {
        setCurrentPostCardStatus(postCardStatus.new);
        
      } else if(props.post.createdDate !== props.post.modifiedDate && props.post.subscribed) {
        setCurrentPostCardStatus(postCardStatus.updated);
      }
      setLastModifiedDate(props.post.modifiedDate);
    }
  
    return () => {
    }
  }, [props.post]);
  

  const observer = useMemo(
    () =>
      new IntersectionObserver(([entry]) => {
        setIsIntersecting(entry.isIntersecting);
      },
      ),
    [],
  );

  useEffect(() => {
    if(postCardRef.current) {
      observer.observe(postCardRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [postCardRef, observer]);

  const styleSelector = (postType: PostTypesValues) => {
    switch(postType) {
      case PostTypes.bible :
        return 'post-type-bible';
      case PostTypes.prayer :
        return 'post-type-prayer';
      case PostTypes.fellowship :
        return 'post-type-fellowship';
    }
  }

  const iconSelector = (postType: PostTypesValues) => {
    switch(postType) {
      case PostTypes.bible :
        return <IonIcon size='large' slot="start" icon={bookSharp}></IonIcon>
      case PostTypes.prayer :
        return <IonNote slot="start"><PrayerIcon className="color-secondary post-card-prayer-header-icon" /></IonNote>;
      case PostTypes.fellowship :
        return <IonIcon size='large' slot="start" icon={chatbubbles}></IonIcon>;
    }
  }

  const getLastComments = (post: IFellowshipPost) => {
    const commentsTotal =  post.comments.length;
    let returnedComments: Array<IFellowshipComment> = []
    if(commentsTotal && commentsTotal > 0) {
      const lastCommentIndex = commentsTotal - 1;
      returnedComments = [post.comments[lastCommentIndex]]
    }
    if(commentsTotal && commentsTotal > 1) {
      const secondToLastIndex = commentsTotal - 2;
      returnedComments = [post.comments[secondToLastIndex], ...returnedComments];
    }

    return returnedComments.map(comment => 
      <div key={`comment-${comment.id}`} className="fellowship-post-comment-container"  hidden={
        (props.currentUser.id !== comment.userId && comment.hidden === UserRoles.admin) || comment.hidden === UserRoles.groupMember ? true : false}>
        <div className="fellowship-post-comment-box-author">{GetMemberInfo(comment.userId, props.memberDetails, 'displayName')}</div>
        <div className="fellowship-post-comment-box-date">{new Date(comment.createdDate).toLocaleString()}</div>
        <div className="fellowship-post-comment-box">{truncate(comment.commentText)}</div>
      </div>
    )
  }

  const truncate = (str:string) => {
    const n = 150;
    if (str.length <= n) { return str; }
    const subString = str.substr(0, n-1); 
    return <span>{subString.substr(0, subString.lastIndexOf(" "))}... <b>read more</b></span>;
  };

  const removePost = () => {
    setShowPostMenu(false); 
    setPostMenuEvent(undefined);
    props.removePost(props.post);
  }

  const setStatusUpdated = () => {
    setCurrentPostCardStatus(postCardStatus.updated);
  }

  return (
    <IonCard ref={postCardRef} className={`fellowship-post-card ${styleSelector(props.post.type)} 
      ${(currentPostCardStatus !== postCardStatus.old && isIntersecting) && 'fellowship-post-new'}`}>
      <IonCardHeader>
        <IonToolbar mode='md'>
          {iconSelector(props.post.type)}
          <IonTitle>{GetMemberInfo(props.post.userId, props.memberDetails, 'displayName')} 
            <p>
              {new Date(props.post.createdDate).toLocaleString()}&nbsp;
              {
                currentPostCardStatus === postCardStatus.new && isIntersecting ?
                  <b className='fellowship-post-new-tag bounce-right-appear'> - NEW</b> : 
                currentPostCardStatus === postCardStatus.updated && isIntersecting ? 
                  <b className='fellowship-post-new-tag bounce-right-appear'> - UPDATED</b> :
                  <b></b>
                }
            </p>
          </IonTitle>
          { (props.currentUser.id === props.post.userId || GetMemberInfo(props.currentUser.id, props.memberDetails, 'role') === UserRoles.admin) &&
            <IonIcon className="fellowship-post-menu-icon" slot='end' icon={ellipsisVertical}
            onClick={(e) => { setShowPostMenu(true); setPostMenuEvent(e.nativeEvent)}}></IonIcon>
          }
        </IonToolbar>
      </IonCardHeader>
      <IonCardContent
        onClick={() => history.push(AppRouteInfo[AppRouteKeys.FellowshipComments].path
            .replace(':navigation', 'read-post').replace(':postId', props.post.id), {direction: 'forward'})}
      >
        <IonCardTitle>{props.post.title}</IonCardTitle>
        <p>{props.post.postText.split('\n').map((item, key) => {
            return <span key={key}>{item}<br/></span>
            })}
        </p>
        <PostVideos key={`fellowship-post-video-${props.post.id}`} postId={props.post.id} postText={props.post.postText} thumbNailOnly={true} cssClass={'post-video'}></PostVideos>
      </IonCardContent>
        
      <div className="fellowship-post-footer">
        <div className="fellowship-footer-divider">
          <div className="fellowship-post-notifications" onClick={(e) => { setShowLikesUserList(true); setLikesUserListEvent(e.nativeEvent)}}>
            <IonIcon icon={heart} /><span>{props.post.likesUserIds.length < 20 ? props.post.likesUserIds.length : 'Max'}</span>
            <IonIcon icon={chatboxEllipses} /><span>{props.post.comments.length}</span>
          </div>
          <div className="fellowship-post-add-prayer">
          { props.post.type === PostTypes.prayer && props.post.userId !== props.currentUser.id &&
            <div onClick={() => props.savePrayer(props.post.title, props.post.postText, props.post.id,  GetMemberInfo(props.post.userId, props.memberDetails, 'displayName'))}>
              <IonIcon icon={addCircle} /><span>add to my prayers</span>
            </div> 
          }
          </div>
        </div>
        { props.post.comments.length > 0 && 
        <div className="fellowship-footer-divider" 
          onClick={() => history.push(AppRouteInfo[AppRouteKeys.FellowshipComments].path
          .replace(":navigation", "read-comments").replace(':postId', props.post.id), {direction: 'forward'})}>
          {getLastComments(props.post)}
          <div className="fellowship-post-view-comments">
            <IonIcon icon={chevronDown}></IonIcon>
            read more comments
          </div>
        </div>
        }
        <div className="fellowship-post-engagement">
          <div className="fellowship-post-add-comment" 
            onClick={() => history.push(AppRouteInfo[AppRouteKeys.FellowshipComments].path
              .replace(":navigation", "add-comment").replace(':postId', props.post.id), {direction: 'forward'})}
          >
            <div className="comment-button-text">write comment...</div>
            <div className="comment-button-icon"><IonIcon icon={send} /></div>
          </div>
          <div className="fellowship-post-add-like">
            <IonIcon icon={props.post.likesUserIds.find(id => id === props.currentUser.id)?.length ? heart : heartOutline} 
              onClick={() => {
                props.toggleLike(props.post);
                !props.post.subscribed && setStatusUpdated();
              }} />
          </div>
        </div>
      </div>
      <IonPopover
          mode="md"
          isOpen={showLikesUserList}
          event={likesUserListEvent}
          onDidDismiss={() => {
              setShowLikesUserList(false); 
              setLikesUserListEvent(undefined);
          }}
      > 
        <IonList lines="none">
        {props.post.likesUserIds.map(userId =>
          <IonItem key={`like-${props.post.id}-${userId}`}>
            <IonIcon icon={heart} slot="start"></IonIcon>
            <IonLabel>{GetMemberInfo(userId, props.memberDetails, 'displayName')}</IonLabel>
          </IonItem>
        )}
        </IonList>
      </IonPopover>
      <IonPopover
          mode="md"
          isOpen={showPostMenu}
          event={postMenuEvent}
          dismissOnSelect={true}
          onDidDismiss={() => {
              setShowPostMenu(false); 
              setPostMenuEvent(undefined);
          }}
      > 
        <IonList lines="none">
        { (props.currentUser.id === props.post.userId || GetMemberInfo(props.currentUser.id, props.memberDetails, 'role') === UserRoles.admin) &&
          <IonItem className="cursor-pointer" onClick={removePost}>
            <IonIcon icon={trashBin} slot="start"></IonIcon>
            <IonLabel>REMOVE POST</IonLabel>
          </IonItem> }
        { props.currentUser.id === props.post.userId &&
          <IonItem className="cursor-pointer"
            onClick={() => history.push(AppRouteInfo[AppRouteKeys.FellowshipEditPost].path
            .replace(':postId', props.post.id), {direction: 'forward'})}>
            <IonIcon icon={chatboxEllipses} slot="start"></IonIcon>
            <IonLabel>EDIT POST</IonLabel>
          </IonItem> }
        </IonList>
      </IonPopover>
    </IonCard>
  );
};

export default FellowshipPostCard;